import { Injectable } from '@angular/core';

import { filter, Observable, ReplaySubject, Subject, switchMap, take } from 'rxjs';

import { FeatureFlagService, GqlService } from '@innovation-os-ng/core/core';
import { EntityConfigType } from '@innovation-os-ng/core/shared';
import { EntityConfigRelationType } from '../model/relationType';

import { EC_GQL_ID } from '../gql-dbo-id';
import controls from '../graphql/queries/controls';
import controlsOverlay from '../graphql/queries/controlsOverlay';
import elementTypes from '../graphql/queries/elementTypes';
import fields from '../graphql/queries/fields';
import fieldsOverlay from '../graphql/queries/fieldsOverlay';
import relationTypes from '../graphql/queries/relationTypes';
import { ErrorUiService, ErrorUIType } from './error-ui.service';

import {
  ControlPropertyValue,
  EntityConfigControl,
  EntityConfigControlChild,
  EntityConfigField,
  FeatureFlags,
  PropertySuffix
} from '@innovation-os-ng/core/shared';
import { skipper, SpaceSelectors } from '@innovation-os-ng/core/state';
import { Store } from '@ngrx/store';
import { ElementTypeService } from './element-type/element-type.service';
import { EntityConfigControlService } from './entity-config-control.service';
import { EntityConfigFieldService } from './entity-config-field.service';

@Injectable({
  providedIn: 'root'
})
export class EntityConfigService {
  private fields: {
    [tenantUri: string]: ReplaySubject<Array<EntityConfigField>>;
  } = {};

  private fieldsOverlay: {
    [spaceUri: string]: ReplaySubject<Array<EntityConfigField>>;
  } = {};

  private controls: {
    [tenantUri: string]: ReplaySubject<Array<EntityConfigControl>>;
  } = {};

  private controlsOverlay: {
    [spaceUri: string]: ReplaySubject<Array<EntityConfigControl>>;
  } = {};

  private elementTypes: {
    [tenantUri: string]: ReplaySubject<Array<EntityConfigType>>;
  } = {};

  private elementTypesOverlay: {
    [spaceUri: string]: ReplaySubject<Array<EntityConfigType>>;
  } = {};

  private relationTypes: {
    [tenantUri: string]: {
      [spaceUri: string]: ReplaySubject<Array<EntityConfigRelationType>>;
    };
  } = {};

  /**
   * List of enabled Feature Flags.
   */
  private featureFlagList: Array<string> = [];

  private useWorkspaceLevelEC: boolean;
  private initializationSubject: Subject<void> = new ReplaySubject(1);
  private initialized$: Observable<void> = this.initializationSubject.asObservable();
  private currentspaceUri: string;
  constructor(
    private readonly gqlService: GqlService,
    private readonly errorUiService: ErrorUiService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly elementTypeService: ElementTypeService,
    private readonly entityConfigControlService: EntityConfigControlService,
    private readonly entityConfigFieldService: EntityConfigFieldService,
    private readonly store: Store
  ) {
    this.featureFlagService.isFeatureEnabled(FeatureFlags.WORKSPACE_LEVEL_ELEMENT_CONFIG).then((enabled: boolean) => {
      this.useWorkspaceLevelEC = enabled;
      this.initializationSubject.next();
      this.initializationSubject.complete();
    });

    this.store
      .select(SpaceSelectors.selectCurrentSpaceUri)
      .pipe(skipper())
      .subscribe({
        next: (currentspaceUri: string) => {
          this.currentspaceUri = currentspaceUri;
        }
      });
  }

  private loadConfiguration(tenantUri: string) {
    this.loadFieldsTenant(tenantUri);
    this.loadControlsTenant(tenantUri);
    this.loadElementTypesTenant(tenantUri);
  }

  private loadConfigurationOverlay(tenantUri: string, spaceUri: string) {
    this.loadControlsOverlay(spaceUri);
    this.loadFieldsOverlay(spaceUri);
    this.loadElementTypesOverlay(tenantUri, spaceUri);
  }

  private loadFieldsTenant(tenantUri: string) {
    this.fields[tenantUri] = new ReplaySubject<Array<EntityConfigField>>(1);
    this.gqlService
      .watchQuery<{ fields: Array<EntityConfigField> }, unknown>(
        {
          query: fields,
          variables: {
            tenantUri
          }
        },
        EC_GQL_ID
      )
      .subscribe(
        (res) => {
          if (res.errors) {
            this.errorUiService.notifyError({
              type: ErrorUIType.REQUEST,
              value: res.errors,
              element: null
            });

            this.fields[tenantUri].next([]);
            return;
          }

          if (!res.data) {
            this.fields[tenantUri].next([]);
            return;
          }
          this.fields[tenantUri].next(res.data.fields);
        },
        (err) => {
          this.errorUiService.notifyError({
            type: ErrorUIType.REQUEST,
            value: err
          });
        }
      );
  }

  private loadFieldsOverlay(spaceUri: string) {
    this.fieldsOverlay[spaceUri] = new ReplaySubject<Array<EntityConfigField>>(1);
    this.gqlService
      .watchQuery<{ fieldsOverlay: Array<EntityConfigField> }, unknown>(
        {
          query: fieldsOverlay,
          variables: {
            spaceUri
          }
        },
        EC_GQL_ID
      )
      .subscribe(
        (res) => {
          if (res.errors) {
            this.errorUiService.notifyError({
              type: ErrorUIType.REQUEST,
              value: res.errors,
              element: null
            });

            this.fieldsOverlay[spaceUri].next([]);
            return;
          }

          if (!res.data) {
            this.fieldsOverlay[spaceUri].next([]);
            return;
          }
          this.fieldsOverlay[spaceUri].next(res.data.fieldsOverlay);
        },
        (err) => {
          this.errorUiService.notifyError({
            type: ErrorUIType.REQUEST,
            value: err
          });
        }
      );
  }

  private loadControlsTenant(tenantUri: string) {
    this.controls[tenantUri] = new ReplaySubject<Array<EntityConfigControl>>(1);

    this.gqlService
      .watchQuery<{ controls: Array<EntityConfigControl> }, unknown>(
        {
          query: controls,
          variables: {
            tenantUri
          }
        },
        EC_GQL_ID
      )
      .subscribe(
        async (res) => {
          if (res.errors) {
            this.errorUiService.notifyError({
              type: ErrorUIType.REQUEST,
              value: res.errors,
              element: null
            });

            this.controls[tenantUri].next([]);
            return;
          }

          if (!res.data) {
            this.controls[tenantUri].next([]);
            return;
          }

          // Get mapped controls data.
          const mappedResponseControls: Array<EntityConfigControl> = await this.hideFeatureFlagEnabledControlDescendants(
            res.data?.controls
          );

          this.controls[tenantUri].next(mappedResponseControls);
        },
        (err) => {
          this.errorUiService.notifyError({
            type: ErrorUIType.REQUEST,
            value: err
          });
        }
      );
  }

  private loadControlsOverlay(spaceUri: string) {
    this.controlsOverlay[spaceUri] = new ReplaySubject<Array<EntityConfigControl>>(1);

    this.gqlService
      .watchQuery<{ controlsOverlay: Array<EntityConfigControl> }, unknown>(
        {
          query: controlsOverlay,
          variables: {
            spaceUri
          }
        },
        EC_GQL_ID
      )
      .subscribe(
        async (res) => {
          if (res.errors) {
            this.errorUiService.notifyError({
              type: ErrorUIType.REQUEST,
              value: res.errors,
              element: null
            });

            this.controlsOverlay[spaceUri].next([]);
            return;
          }

          if (!res.data) {
            this.controlsOverlay[spaceUri].next([]);
            return;
          }

          // Get mapped controls data.
          const mappedResponseControls: Array<EntityConfigControl> = await this.hideFeatureFlagEnabledControlDescendants(
            res.data?.controlsOverlay
          );

          this.controlsOverlay[spaceUri].next(mappedResponseControls);
        },
        (err) => {
          this.errorUiService.notifyError({
            type: ErrorUIType.REQUEST,
            value: err
          });
        }
      );
  }

  private loadElementTypesTenant(tenantUri: string) {
    this.elementTypes[tenantUri] = new ReplaySubject<Array<EntityConfigType>>(1);
    this.gqlService
      .watchQuery<{ elementTypes: Array<EntityConfigType> }, unknown>(
        {
          query: elementTypes,
          variables: {
            tenantUri
          }
        },
        EC_GQL_ID
      )
      .subscribe(
        (res) => {
          if (res.errors) {
            this.errorUiService.notifyError({
              type: ErrorUIType.REQUEST,
              value: res.errors,
              element: null
            });

            this.elementTypes[tenantUri].next([]);
            return;
          }

          if (!res.data) {
            this.elementTypes[tenantUri].next([]);
            return;
          }

          this.elementTypes[tenantUri].next(res.data.elementTypes);
        },
        (err) => {
          this.errorUiService.notifyError({
            type: ErrorUIType.REQUEST,
            value: err
          });
        }
      );
  }

  private loadElementTypesOverlay(tenantUri: string, spaceUri: string) {
    this.elementTypesOverlay[spaceUri] = new ReplaySubject<Array<EntityConfigType>>(1);
    this.gqlService
      .watchQuery<{ elementTypes: Array<EntityConfigType> }, unknown>(
        {
          query: elementTypes,
          variables: {
            tenantUri,
            spaceUri
          }
        },
        EC_GQL_ID
      )
      .subscribe(
        (res) => {
          if (res.errors) {
            this.errorUiService.notifyError({
              type: ErrorUIType.REQUEST,
              value: res.errors,
              element: null
            });

            this.elementTypesOverlay[spaceUri].next([]);
            return;
          }

          if (!res.data) {
            this.elementTypesOverlay[spaceUri].next([]);
            return;
          }

          this.elementTypesOverlay[spaceUri].next(res.data.elementTypes);
        },
        (err) => {
          this.errorUiService.notifyError({
            type: ErrorUIType.REQUEST,
            value: err
          });
        }
      );
  }

  private loadRelationTypes(tenantUri: string, spaceUri: string) {
    this.relationTypes[tenantUri] = this.relationTypes[tenantUri] || {};
    this.relationTypes[tenantUri][spaceUri] = new ReplaySubject<Array<EntityConfigRelationType>>(1);
    this.gqlService
      .watchQuery<{ relationTypes: Array<EntityConfigRelationType> }, unknown>(
        {
          query: relationTypes,
          variables: {
            tenantUri,
            spaceUri
          }
        },
        EC_GQL_ID
      )
      .subscribe(
        (res) => {
          if (res.errors) {
            this.errorUiService.notifyError({
              type: ErrorUIType.REQUEST,
              value: res.errors,
              element: null
            });

            this.relationTypes[tenantUri][spaceUri].next([]);
            return;
          }

          if (!res.data) {
            this.relationTypes[tenantUri][spaceUri].next([]);
            return;
          }

          this.relationTypes[tenantUri][spaceUri].next(res.data.relationTypes);
        },
        (err) => {
          this.errorUiService.notifyError({
            type: ErrorUIType.REQUEST,
            value: err
          });
        }
      );
  }

  /**
   * Filter out the descendants if it is has feature flag check
   * and the flag is disabled.
   *
   * @param controlsParam - Original controls data.
   *
   * @returns - Returns the modified controls.
   */
  private async hideFeatureFlagEnabledControlDescendants(controlsParam: Array<EntityConfigControl>): Promise<Array<EntityConfigControl>> {
    // If no controls then return as it is.
    if (!controlsParam?.length) {
      return controlsParam;
    }

    if (!this.featureFlagList?.length) {
      // Fetch the FF list
      this.featureFlagList = await this.featureFlagService.getFeatureFlags();
    }

    /**
     * We need to do this because we want to mutate the data.
     * Without this we get error (readonly cannot be updated.)
     */
    const deepClonedControls: Array<EntityConfigControl> = JSON.parse(JSON.stringify(controlsParam));

    /**
     * Loop through every descendants and update the disable to true if it is hidden.
     *
     * We needed this check because we don't have a check for descendants if it has
     * FF feature.
     *
     * This is done to hide the smart rating fields.
     */
    const modifiedControls: Array<EntityConfigControl> = deepClonedControls.map((control: EntityConfigControl) => {
      // We map and modify the descendant based on FF
      control.descendants = control.descendants.map((child: EntityConfigControlChild) => {
        /**
         * If already disabled there is no need to check as,
         * we are handling the case where the FF is disabled but
         * still disable property is not set to true.
         */
        if (child.disabled) {
          return child;
        }

        /**
         * Get all the properties of child control so that we can access propertyValues and get FF.
         *
         * The original controls array has all the control separately available as well.
         */
        const childProperty: EntityConfigControl = controlsParam.find((c) => c?.uri === child?.uri);

        // Get the FF property from the child's propertyValues;
        const featureFlagProperty: ControlPropertyValue = childProperty?.propertyValues?.find((p) =>
          p.controlTypePropertyUri.endsWith(PropertySuffix.FEATURE_FLAG)
        );

        // If FF does not exits then, it is not protected by FF so return as it is.
        if (!featureFlagProperty) {
          return child;
        }

        // If FF property incudes then check if FF is enabled and toggle visibility based on that.
        return {
          ...child,
          disabled: !this.featureFlagList.includes(featureFlagProperty?.value)
        };
      });

      // Return the modified control.
      return control;
    });

    // Return the modified controls data.
    return modifiedControls;
  }

  /**
   *
   * PUBLIC API of this SERVICE
   *
   */

  /**
   *
   * @param tenantUri
   */
  public getFields(tenantUri: string, spaceUri?: string): Observable<Array<EntityConfigField>> {
    // wait until the useWorkspaceLevelEC have been initialized
    return this.initialized$.pipe(
      filter(() => {
        return this.useWorkspaceLevelEC !== undefined;
      }),
      take(1),
      switchMap(() => {
        if (this.useWorkspaceLevelEC) {
          console.assert(!!this.currentspaceUri, 'currentSpaceUri should not be null');
          spaceUri = spaceUri || this.currentspaceUri;
          return this.entityConfigFieldService.getFields(spaceUri);
        }

        /*
          Overlays
        */
        // If the spaceUri is provided we want to get controls from the space only
        if (spaceUri) {
          if (!this.fieldsOverlay[spaceUri]) {
            this.loadConfigurationOverlay(tenantUri, spaceUri);
          }
          return this.fieldsOverlay[spaceUri].asObservable();
        }

        /*
          Tenant Level
        */

        if (!this.fields[tenantUri]) {
          this.loadConfiguration(tenantUri);
        }
        return this.fields[tenantUri].asObservable();
      })
    );
  }

  /**
   * @param tenantUri
   */
  public getControls(tenantUri: string, spaceUri?: string, refreshCache: boolean = false): Observable<Array<EntityConfigControl>> {
    // wait until the useWorkspaceLevelEC have been initialized
    return this.initialized$.pipe(
      filter(() => {
        return this.useWorkspaceLevelEC !== undefined;
      }),
      take(1),
      switchMap(() => {
        if (this.useWorkspaceLevelEC) {
          console.assert(!!this.currentspaceUri, 'currentSpaceUri should not be null');
          spaceUri = spaceUri || this.currentspaceUri;
          return this.entityConfigControlService.getControls(spaceUri, refreshCache);
        }

        /*
          Overlays
        */
        // If the spaceUri is provided we want to get controls from the space only
        if (spaceUri) {
          if (!this.controlsOverlay[spaceUri]) {
            this.loadConfigurationOverlay(tenantUri, spaceUri);
          }
          return this.controlsOverlay[spaceUri].asObservable();
        }

        /*
          Tenant Level
        */
        if (!this.controls[tenantUri]) {
          this.loadConfiguration(tenantUri);
        }
        return this.controls[tenantUri].asObservable();
      })
    );
  }

  /**
   *
   * @param tenantUri
   * @param spaceUri
   */
  public getElementTypes(tenantUri: string, spaceUri: string = ''): Observable<Array<EntityConfigType>> {
    // wait until the useWorkspaceLevelEC have been initialized
    return this.initialized$.pipe(
      filter(() => {
        return this.useWorkspaceLevelEC !== undefined;
      }),
      take(1),
      switchMap(() => {
        if (this.useWorkspaceLevelEC) {
          console.assert(!!this.currentspaceUri, 'currentSpaceUri should not be null');
          spaceUri = spaceUri || this.currentspaceUri;
          return this.elementTypeService.getAllElementTypes(spaceUri);
        }

        /*
        Overlays
      */
        // If the spaceUri is provided we want to get controls from the space only
        if (spaceUri) {
          if (!this.elementTypesOverlay[spaceUri]) {
            this.loadConfigurationOverlay(tenantUri, spaceUri);
          }
          return this.elementTypesOverlay[spaceUri].asObservable();
        }

        /*
        Tenant Level
      */

        if (!this.elementTypes[tenantUri]) {
          this.loadConfiguration(tenantUri);
        }
        return this.elementTypes[tenantUri].asObservable();
      })
    );
  }

  /**
   *
   * @param tenantUri
   * @param spaceUri
   */
  public getRelationTypes(tenantUri: string, spaceUri: string = ''): Observable<Array<EntityConfigRelationType>> {
    if (!this.relationTypes[tenantUri] || !this.relationTypes[tenantUri][spaceUri]) {
      this.loadRelationTypes(tenantUri, spaceUri);
    }
    return this.relationTypes[tenantUri][spaceUri].asObservable();
  }
}
